import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Calendar,
  DollarSign,
  Download,
  FileCheck,
  Hash,
  Loader2,
  Package,
  Printer,
  Search,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/api/client';
import PDFViewerDialog from '@/components/mail/PDFViewerDialog';

export default function ShippingPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('active');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingRefund, setPendingRefund] = useState(null);
  /**
   * Set when a delete was stopped because the carrier refused some of the
   * refunds. Carries the list, so the second dialog can name what is being
   * written off rather than asking "are you sure?" about an unnamed total.
   */
  const [deleteBlockedBy, setDeleteBlockedBy] = useState(null);
  const [viewing, setViewing] = useState(null);

  const archived = tab === 'archived';

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders', { archived, search }],
    queryFn: () =>
      api.request(
        `/orders?archived=${archived}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
      ),
  });

  /**
   * What a confirmation dialog should show, including while it closes.
   *
   * Radix animates the dialog out, so it is still on screen for a moment after
   * the state feeding it has been cleared — long enough to flash an empty
   * order name at whoever just pressed the button.
   */
  const lastRefundTarget = useRef(null);
  if (pendingRefund) lastRefundTarget.current = pendingRefund;
  const refundTarget = pendingRefund ?? lastRefundTarget.current;

  const lastDeleteTarget = useRef(null);
  if (pendingDelete) lastDeleteTarget.current = pendingDelete;
  const deleteTarget = pendingDelete ?? lastDeleteTarget.current;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['orders'] });

  const archiveMutation = useMutation({
    mutationFn: (order) => api.request(`/orders/${order.id}/archive`, { method: 'POST' }),
    onSuccess: (data) => {
      invalidate();
      toast.success(data.archived ? 'Order archived' : 'Order restored');
    },
    onError: (error) => toast.error(error.message ?? 'Could not archive that order'),
  });

  const refundMutation = useMutation({
    mutationFn: (order) => api.request(`/orders/${order.id}/refund`, { method: 'POST' }),
    onSuccess: (data) => {
      invalidate();
      const { succeeded, refused } = data.refundSummary;

      if (refused.length > 0) {
        toast.warning(
          `${succeeded} refunded. ${refused.length} could not be — open the order to see which.`,
        );
      } else {
        // "Requested", not "refunded": USPS reviews these in its own time.
        toast.success(`Refund requested for ${succeeded} label(s)`);
      }
    },
    onError: (error) => toast.error(error.message ?? 'Could not refund that order'),
    onSettled: () => setPendingRefund(null),
  });

  /**
   * Delete refunds first, and stops if the carrier refuses any of them.
   *
   * An order holds the tracking numbers for every label in the run. Deleting
   * while a refund stands refused throws away the only means of chasing that
   * postage — twenty labels at a time, on a bulk run.
   */
  const deleteMutation = useMutation({
    mutationFn: ({ order, force }) =>
      api.request(`/orders/${order.id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
    onSuccess: (data) => {
      invalidate();
      setPendingDelete(null);
      setDeleteBlockedBy(null);

      const { successful, failed } = data.refundSummary;

      if (data.forced) {
        toast.warning(`Order deleted. ${failed} label(s) were not refunded and the cost stands.`);
      } else if (successful === 0) {
        toast.success('Order deleted');
      } else {
        toast.success(`Order deleted and ${successful} label(s) refunded`);
      }
    },
    onError: (error, variables) => {
      // 409 is the carrier refusing, which is a decision to put back to the
      // operator. Anything else is a genuine failure.
      if (error.status === 409 && !variables.force) {
        setDeleteBlockedBy(error.details ?? { refused: [], refusedTotal: '0' });
        return;
      }

      toast.error(error.message ?? 'Could not delete that order');
      setPendingDelete(null);
      setDeleteBlockedBy(null);
    },
  });

  const manifestMutation = useMutation({
    mutationFn: (order) => api.request(`/orders/${order.id}/manifest`, { method: 'POST' }),
    onSuccess: () => {
      invalidate();
      toast.success('Manifest created');
    },
    onError: (error) => toast.error(error.message ?? 'Could not create the manifest'),
  });

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Shipping orders</h1>
        <p className="text-slate-600">Every bulk run, and what it cost</p>
      </div>

      <Card className="border-none shadow-lg">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search by order name or number…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10 h-11"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="archived">Archived</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : orders.length === 0 ? (
            <Card className="border-none shadow-lg">
              <CardContent className="p-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  {search
                    ? 'No orders match that search'
                    : archived
                      ? 'Nothing archived'
                      : 'No orders yet'}
                </h3>
                {!search && !archived && (
                  <Button asChild className="bg-blue-600 hover:bg-blue-700 mt-2">
                    <Link to="/upload">Start a bulk upload</Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {orders.map((order) => (
                <li key={order.id}>
                  <OrderCard
                    order={order}
                    onArchive={() => archiveMutation.mutate(order)}
                    onRefund={() => setPendingRefund(order)}
                    onDelete={() => {
                      setDeleteBlockedBy(null);
                      setPendingDelete(order);
                    }}
                    onManifest={() => manifestMutation.mutate(order)}
                    manifestPending={manifestMutation.isPending}
                    onPrint={() =>
                      setViewing({
                        url: `/api/documents/orders/${order.id}/labels`,
                        title: `${order.orderName} — all labels`,
                      })
                    }
                    onPrintManifest={() =>
                      setViewing({
                        url: `/api/documents/orders/${order.id}/manifest`,
                        title: `${order.orderName} — manifest`,
                      })
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog
        open={Boolean(pendingRefund)}
        onOpenChange={(open) => !open && setPendingRefund(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refund “{refundTarget?.orderName}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This asks the carrier to refund every label in this order that has not been refunded
              already — {(refundTarget?._count?.parcels ?? 0) - (refundTarget?.refundedParcels ?? 0)}{' '}
              of {refundTarget?._count?.parcels ?? 0}. Carriers take a few days to process a refund,
              and will not refund a label that has already been scanned.
              <br />
              <br />
              The order stays in the list, so you can still see what it cost and which labels the
              carrier would not take back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => refundMutation.mutate(pendingRefund)}
              disabled={refundMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {refundMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Refund all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/*
        Delete refunds first. The second state — after the carrier has refused
        some of them — is a different question, so it gets its own wording and
        names the labels rather than only a count.
      */}
      <AlertDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
            setDeleteBlockedBy(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteBlockedBy
                ? 'Some labels could not be refunded'
                : `Delete “${deleteTarget?.orderName}”?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteBlockedBy ? (
                <>
                  The carrier would not take back {deleteBlockedBy.refused?.length ?? 0} label(s),
                  worth ${deleteBlockedBy.refusedTotal ?? '0.00'}.
                  <br />
                  <br />
                  Deleting the order now removes the only record of their tracking numbers, and
                  that money cannot be chased afterwards.
                  {deleteBlockedBy.refused?.length > 0 && (
                    <span className="mt-3 block max-h-40 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2 text-xs">
                      {deleteBlockedBy.refused.map((item) => (
                        <span key={item.parcelId} className="block truncate">
                          {item.recipient} — ${Number(item.cost).toFixed(2)}
                        </span>
                      ))}
                    </span>
                  )}
                </>
              ) : (
                <>
                  This asks the carrier to refund all {deleteTarget?._count?.parcels ?? 0} label(s),
                  then deletes the order, its history, and its stored labels for good.
                  <br />
                  <br />
                  If the carrier refuses any of them, nothing is deleted and you will be asked
                  again.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                // Kept open on the first refusal so the second question can be
                // asked in place; Radix would otherwise close on any action.
                event.preventDefault();
                deleteMutation.mutate({
                  order: pendingDelete,
                  force: Boolean(deleteBlockedBy),
                });
              }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {deleteBlockedBy ? 'Delete anyway and write off the cost' : 'Refund and delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {viewing && (
        <PDFViewerDialog
          open
          onClose={() => setViewing(null)}
          documentUrl={viewing.url}
          title={viewing.title}
          parcelIds={viewing.parcelIds ?? []}
        />
      )}
    </div>
  );
}

function OrderCard({
  order,
  onArchive,
  onRefund,
  onDelete,
  onManifest,
  onPrint,
  onPrintManifest,
  manifestPending,
}) {
  const statusStyles = {
    COMPLETED: 'bg-green-100 text-green-800 border-green-200',
    PROCESSING: 'bg-blue-100 text-blue-800 border-blue-200',
    FAILED: 'bg-red-100 text-red-800 border-red-200',
  };

  const run = order.batchRuns?.[0];
  const stillRunning = run && ['VALIDATING', 'PURCHASING', 'NEEDS_REVIEW', 'READY'].includes(run.status);

  const parcelCount = order._count?.parcels ?? 0;
  const refunded = order.refundedParcels ?? 0;
  const refused = order.refundsRefused ?? 0;
  // Nothing left to ask the carrier about, so the Refund button would only
  // produce a 409. Delete is still offered — that is the remaining decision.
  const fullyRefunded = parcelCount > 0 && refunded >= parcelCount;

  return (
    <Card className="border-none shadow-lg hover:shadow-xl transition-shadow h-full">
      <CardContent className="p-5 flex flex-col h-full">
        <div className="flex items-start justify-between mb-3">
          <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <Package className="w-5 h-5 text-white" />
          </div>
          {/*
            A refund refused by the carrier outranks the run's own status here.
            "completed" on an order whose postage is unrecoverable is true and
            useless; which labels need chasing is the thing worth surfacing.
          */}
          {refused > 0 ? (
            <span
              className="px-2.5 py-1 rounded-full text-xs font-medium border bg-red-100 text-red-800 border-red-200"
              title={`${refused} label(s) the carrier would not refund`}
            >
              {refused} refund{refused === 1 ? '' : 's'} refused
            </span>
          ) : fullyRefunded ? (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium border bg-slate-100 text-slate-700 border-slate-200">
              refunded
            </span>
          ) : (
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                statusStyles[order.status] ?? statusStyles.PROCESSING
              }`}
            >
              {order.status.toLowerCase()}
            </span>
          )}
        </div>

        <Link to={`/orders/${order.id}`} className="group">
          <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
            {order.orderName}
          </h3>
        </Link>

        <div className="space-y-1.5 text-sm text-slate-600 mt-2 mb-4 flex-1">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 shrink-0" />
            <span className="truncate">{order.orderNumber}</span>
          </div>
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 shrink-0" />
            <span>
              {parcelCount} parcels
              {refunded > 0 && !fullyRefunded && (
                <span className="text-slate-500"> · {refunded} refunded</span>
              )}
            </span>
          </div>
          <div
            className={`flex items-center gap-2 font-semibold ${
              fullyRefunded && refused === 0 ? 'text-slate-400' : 'text-green-700'
            }`}
          >
            <DollarSign className="w-4 h-4 shrink-0" />
            {/* Struck through only when the money is actually coming back. */}
            <span className={fullyRefunded && refused === 0 ? 'line-through' : ''}>
              ${Number(order.totalCost).toFixed(2)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 shrink-0" />
            <span>{format(new Date(order.createdAt), 'MMM d, yyyy')}</span>
          </div>
        </div>

        {stillRunning && (
          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-3">
            Run in progress — open the order to see where it is
          </p>
        )}

        <div className="flex flex-wrap gap-1.5">
          {order.hasLabels && (
            <Button variant="outline" size="sm" onClick={onPrint} className="gap-1">
              <Printer className="w-3.5 h-3.5" />
              Labels
            </Button>
          )}

          {order.hasManifest ? (
            <Button variant="outline" size="sm" onClick={onPrintManifest} className="gap-1">
              <FileCheck className="w-3.5 h-3.5" />
              Manifest
            </Button>
          ) : (
            order.status === 'COMPLETED' && (
              <Button
                variant="outline"
                size="sm"
                onClick={onManifest}
                disabled={manifestPending}
                className="gap-1"
              >
                {manifestPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <FileCheck className="w-3.5 h-3.5" />
                )}
                Manifest
              </Button>
            )
          )}

          <Button variant="outline" size="sm" asChild className="gap-1">
            <a href={`/api/orders/${order.id}/export`} download>
              <Download className="w-3.5 h-3.5" />
              CSV
            </a>
          </Button>

          <Button variant="outline" size="sm" onClick={onArchive} aria-label="Archive">
            {order.archived ? (
              <ArchiveRestore className="w-3.5 h-3.5" />
            ) : (
              <Archive className="w-3.5 h-3.5" />
            )}
          </Button>

          {!fullyRefunded && parcelCount > 0 && (
            <Button variant="outline" size="sm" onClick={onRefund} className="gap-1">
              <Undo2 className="w-3.5 h-3.5" />
              Refund
            </Button>
          )}

          <Button variant="destructive" size="sm" onClick={onDelete} aria-label="Delete">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
